/**
 * The session's own model of what it has dictated (issue #403).
 *
 * WHY THIS IS NOT JUST THE TEXTAREA. The first version treated the bound field
 * as the state: to revise anything it had to clear the field and write it back.
 * That produced two user-visible faults.
 *
 *   1. Text VANISHED for the duration of the cleanup round trip, then
 *      reappeared changed — a full second of the user's words being gone.
 *   2. A cleanup that landed after the user cleared the field wrote the text
 *      back, resurrecting speech they had deliberately deleted.
 *
 * Both follow from the same mistake: if the field is the state, revising means
 * destroying, and a late write cannot tell itself apart from a fresh one.
 *
 * So the buffer keeps SEGMENTS. Each settled chunk is recorded with an id, and
 * the cleanup pass revises that segment IN PLACE when it returns. The rendered
 * text therefore never loses content — the raw words are on screen from the
 * moment they settle, and the tidied version swaps in without a gap.
 *
 * The id is also the mapping the cleanup needs: a response carries the id of
 * the chunk it was asked about, so it can only ever affect that chunk, however
 * late it arrives or however much has been dictated since.
 *
 * ABANDONMENT IS STICKY. Once the user touches the field, this buffer is done:
 * every later write, including cleanups already in flight, is dropped for good.
 * A flag that could un-set is what let the zombie text through.
 *
 * Pure and synchronous; no Angular, no DOM.
 */

interface Segment {
    /** Positive for dictated chunks (revisable by the cleanup); NEGATIVE for
     *  characters the user typed, which are glued in place and never revised. */
    id: number;
    text: string;
}

/**
 * Trim spaces and tabs, but NOT newlines.
 *
 * A plain .trim() here silently destroyed every "new line" and "new paragraph"
 * that landed at the end of a chunk — which is most of the times they are used,
 * because a speaker naturally pauses right after saying it, and a pause is
 * exactly where chunks are cut. Also drops the horizontal whitespace the
 * cleanup model leaves in front of a break ("milestone  \n"), which would
 * otherwise show as trailing spaces at the end of a line.
 */
function trimHorizontal(text: string): string {
    return (text ?? "").replace(/^[ \t]+|[ \t]+$/g, "").replace(/[ \t]+\n/g, "\n");
}

/**
 * Append with a single separating space, unless either side already supplies
 * whitespace — a chunk the cleanup turned into a paragraph break must not gain
 * a stray space in front of its newline.
 */
function joinTwo(left: string, right: string): string {
    if (!left) return right;
    if (!right) return left;
    const gap = /\s$/.test(left) || /^\s/.test(right) ? '' : ' ';
    return left + gap + right;
}

/**
 * Characters a user adds at the end of dictated text without meaning to take
 * the field back: a full stop or comma while thinking, a space, a newline.
 * Typing any of these must NOT stop the microphone — the previous behaviour,
 * where any keystroke abandoned the session, made the mic feel hair-trigger.
 */
const BENIGN_TRAILING = /^[\s.,!?]*$/;

/** What happened to the field since this buffer last wrote it. */
export type FieldChange = "none" | "benign" | "foreign";

export class TranscriptBuffer {
    /** Whatever was in the field when dictation started. */
    private base = '';
    /** Settled chunks, in spoken order. Revisable by id. */
    private segments: Segment[] = [];
    /** The utterance currently being spoken. Replaced wholesale by each delta. */
    private interim = '';
    /** The exact string this buffer last produced, so it can spot foreign values. */
    private lastWritten: string | null = null;
    /** Once true, this buffer never writes again. */
    private abandoned = false;
    /** Ids for typed runs are negative, so reviseSegment can never touch them. */
    private nextTypedId = -1;

    /** Begin a session against the field's current contents. */
    start(current: string): void {
        this.base = (current ?? '').trimEnd();
        this.segments = [];
        this.interim = '';
        this.nextTypedId = -1;
        this.lastWritten = null;
        this.abandoned = false;
    }

    /**
     * What the user did to the field since we last wrote it. Pure: asking
     * changes nothing, so the caller may ask on every digest.
     *
     *   none    — exactly what we wrote
     *   benign  — only trailing punctuation or whitespace added, or removed
     *             again from what we previously absorbed. Dictation continues.
     *   foreign — anything else. The user has taken the field back.
     *
     * The benign case exists because typing "." or a space mid-thought is not
     * an edit, and killing the microphone for it made the feature feel brittle.
     * A deletion only counts as benign when it comes out of the tail we
     * absorbed — we can un-take what we took, but deleting into our own
     * transcript is a real edit.
     */
    classifyChange(current: string): FieldChange {
        const value = current ?? '';
        if (this.lastWritten === null) return 'none';
        if (value === this.lastWritten) return 'none';

        if (value.length > this.lastWritten.length) {
            if (value.indexOf(this.lastWritten) !== 0) return 'foreign';
            return BENIGN_TRAILING.test(value.slice(this.lastWritten.length)) ? 'benign' : 'foreign';
        }

        if (this.lastWritten.indexOf(value) !== 0) return 'foreign';
        const removed = this.lastWritten.slice(value.length);
        if (!BENIGN_TRAILING.test(removed)) return 'foreign';
        return removed.length <= this.typedTailLength() ? 'benign' : 'foreign';
    }

    /** How much of the end is text the user typed, and can therefore un-type. */
    private typedTailLength(): number {
        const last = this.segments[this.segments.length - 1];
        return last && last.id < 0 ? last.text.length : 0;
    }

    /**
     * Take a benign trailing edit into the model so dictation carries on after
     * it. Only meaningful when classifyChange returned "benign".
     */
    absorbBenign(current: string): void {
        const value = current ?? '';
        if (this.lastWritten === null) return;
        const last = this.segments[this.segments.length - 1];

        if (value.length >= this.lastWritten.length) {
            const extra = value.slice(this.lastWritten.length);
            // Recorded as a segment rather than a floating tail, so it keeps its
            // POSITION: speech dictated afterwards lands after the typed full
            // stop, not in front of it.
            if (last && last.id < 0) last.text += extra;
            else this.segments.push({ id: this.nextTypedId--, text: extra });
        } else if (last && last.id < 0) {
            const removed = this.lastWritten.length - value.length;
            last.text = last.text.slice(0, Math.max(0, last.text.length - removed));
            if (!last.text) this.segments.pop();
        }
        this.lastWritten = value;
    }

    /** Stop writing, permanently. The user has taken the field back. */
    abandon(): void {
        this.abandoned = true;
        this.interim = '';
    }

    get isAbandoned(): boolean {
        return this.abandoned;
    }

    /** Replace the in-flight utterance. */
    setInterim(text: string): void {
        this.interim = trimHorizontal(text);
    }

    /**
     * Record a settled chunk under `id`, clearing the interim it came from.
     * The text shown is the RAW transcript: it appears immediately and is
     * revised later, rather than waiting on the cleanup and showing nothing.
     */
    addSegment(id: number, text: string): void {
        const settled = trimHorizontal(text);
        this.interim = '';
        if (!settled) return;
        this.segments.push({ id, text: settled });
    }

    /**
     * Swap a segment's text for its cleaned version. Returns false when the id
     * is unknown — a response for a segment from an abandoned session, say, or
     * one that never settled — so the caller can skip a pointless write.
     */
    reviseSegment(id: number, text: string): boolean {
        const cleaned = trimHorizontal(text);
        if (!cleaned || id < 0) return false;
        for (const segment of this.segments) {
            if (segment.id === id) {
                if (segment.text === cleaned) return false;
                segment.text = cleaned;
                return true;
            }
        }
        return false;
    }

    /** The value the field should now hold. */
    render(): string {
        let out = this.base;
        for (const segment of this.segments) {
            // Typed characters are glued hard against what precedes them — the
            // user put their full stop against the word, not a space away.
            out = segment.id < 0 ? out + segment.text : joinTwo(out, segment.text);
        }
        return joinTwo(out, this.interim);
    }

    /**
     * Render, remember, and hand the result to the caller's setter — unless
     * abandoned, in which case nothing is written at all. The remembering is
     * what lets `isForeign` tell our writes from the user's.
     */
    writeTo(setter: (value: string) => void): void {
        if (this.abandoned) return;
        const value = this.render();
        this.lastWritten = value;
        setter(value);
    }

    /** Drop the in-flight utterance without settling it (session ended mid-word). */
    discardInterim(): void {
        this.interim = '';
    }

    /** True when this session has produced no text at all. */
    get isEmpty(): boolean {
        for (const segment of this.segments) if (segment.id > 0) return false;
        return this.interim.length === 0;
    }
}
